import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { mediaQueryLarge, getIOSVersion } from "@theme/utilities";
export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const dataUrl = this.dataset.productUrl;
    if (dataUrl) {
      return dataUrl;
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest("product-card"));
    const hotspotProduct = /** @type {import('../components/product-hotspot').ProductHotspotComponent | null} */ (
      this.closest("product-hotspot-component")
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return "";

    const url = new URL(productLink.href);

    if (url.searchParams.has("variant")) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set("variant", selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest("product-card"));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById("quick-add-drawer-content");
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector("variant-picker"));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Capture PLP card thumb order synchronously before any async work so we
    // can restore it in the modal regardless of which color is active on the card.
    const plpThumbContainer = this.closest("[data-product-card]")?.querySelector("[data-plp-variant-thumbs]");
    const plpThumbOrder = plpThumbContainer
      ? [...plpThumbContainer.children]
          .filter((el) => el.hasAttribute("data-plp-thumb"))
          .map((el) => /** @type {HTMLElement} */ (el).dataset.productUrl ?? "")
          .filter(Boolean)
      : [];

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector("[data-product-grid-content]");
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      // Restore PLP thumb order so the modal always shows colours in the same
      // sequence as the card, even when a different colour is active.
      if (plpThumbOrder.length > 0) {
        const modalContent = document.getElementById("quick-add-drawer-content");
        const quickAddDialog = /** @type {QuickAddDialog | null} */ (document.getElementById("quick-add-dialog"));
        quickAddDialog?.restoreThumbOrder(modalContent, plpThumbOrder, currentUrl);
      }
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute("stay-visible", false), {
      once: true
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(productPageUrl, window.location.origin);
      url.searchParams.set("section_id", "quick-shop-content");
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById("quick-add-drawer-content");

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest("product-card") !== this.closest("product-card")) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === "1" ? "add" : "choose";
    this.setAttribute("data-quick-add-button", quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #colorThumbAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener("click", this.#handleColorThumbClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#colorThumbAbortController?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener("click", this.#handleColorThumbClick);
  }

  /**
   * Handles clicks on color variant thumbs inside the quick shop modal.
   * Fetches the new product's quick shop content and morphs the modal in place.
   * @param {Event} event
   */
  #handleColorThumbClick = async (event) => {
    const thumb = event.target instanceof Element ? event.target.closest("[data-plp-thumb]") : null;
    if (!(thumb instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const productUrl = thumb.dataset.productUrl;
    if (!productUrl) return;

    // Capture thumb order synchronously before any async work.
    const modalContent = document.getElementById("quick-add-drawer-content");
    const originalThumbOrder = this.#captureThumbOrder(modalContent);

    this.#colorThumbAbortController?.abort();
    this.#colorThumbAbortController = new AbortController();

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("section_id", "quick-shop-content");

      const response = await fetch(url.toString(), { signal: this.#colorThumbAbortController.signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), "text/html");
      const gridElement = html.querySelector("[data-product-grid-content]");

      if (gridElement && modalContent) {
        morph(modalContent, gridElement);

        if (originalThumbOrder.length > 0) {
          this.#restoreThumbOrder(modalContent, originalThumbOrder, productUrl);
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") throw error;
    } finally {
      this.#colorThumbAbortController = null;
    }
  };

  /**
   * Captures the current thumb order (direct-child thumb buttons only, not
   * thumbs inside the overflow panel) as an array of product URLs.
   * @param {Element | null} modalContent
   * @returns {string[]}
   */
  #captureThumbOrder(modalContent) {
    if (!modalContent) return [];
    const thumbContainer = modalContent.querySelector("[data-plp-variant-thumbs]");
    if (!thumbContainer) return [];
    return [...thumbContainer.children]
      .filter((el) => el.hasAttribute("data-plp-thumb"))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.productUrl ?? "")
      .filter(Boolean);
  }

  /**
   * Restores the thumb order after a morph and updates aria-current state.
   * Only operates on direct-child thumb buttons so the overflow panel and its
   * toggle button remain in place.
   * @param {Element} modalContent
   * @param {string[]} originalOrder - Product URLs in the order they had before morph
   * @param {string} activeProductUrl - URL of the product that was just selected
   */
  #restoreThumbOrder(modalContent, originalOrder, activeProductUrl) {
    const thumbContainer = modalContent.querySelector("[data-plp-variant-thumbs]");
    if (!thumbContainer) return;

    const thumbs = [...thumbContainer.children].filter((el) => el.hasAttribute("data-plp-thumb"));
    /** @type {Map<string, Element>} */
    const thumbMap = new Map(thumbs.map((el) => [/** @type {HTMLElement} */ (el).dataset.productUrl, el]));

    // Update active state
    for (const t of thumbs) {
      const isActive = /** @type {HTMLElement} */ (t).dataset.productUrl === activeProductUrl;
      t.setAttribute("aria-current", String(isActive));
      t.setAttribute("data-plp-thumb", isActive ? "initial" : "variant");
    }

    // The first non-thumb child (overflow div, toggle button, …) acts as the
    // insertion point so those elements stay at the end.
    const insertionPoint = [...thumbContainer.children].find((el) => !el.hasAttribute("data-plp-thumb")) ?? null;

    // Re-insert in original order
    for (const url of originalOrder) {
      const t = thumbMap.get(url);
      if (t) thumbContainer.insertBefore(t, insertionPoint);
    }

    // Append any thumb not in the original order (e.g. a new variant)
    for (const t of thumbs) {
      if (!originalOrder.includes(/** @type {HTMLElement} */ (t).dataset.productUrl ?? "")) {
        thumbContainer.insertBefore(t, insertionPoint);
      }
    }
  }

  /**
   * Public proxy so QuickAddComponent can restore thumb order after the
   * initial modal open (where the source of truth is the PLP card, not the modal).
   * @param {Element | null} modalContent
   * @param {string[]} originalOrder
   * @param {string} activeProductUrl
   */
  restoreThumbOrder(modalContent, originalOrder, activeProductUrl) {
    if (!modalContent) return;
    this.#restoreThumbOrder(modalContent, originalOrder, activeProductUrl);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector(".view-product-title a"));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector(".product-header a"));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector("#ResultsList [product-grid-view]:not([hidden])");
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
